import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, ToggleLeft, ToggleRight, Plus, Trash2,
  ChevronDown, ChevronRight, Edit3, Shield, Flame, LogOut,
  CheckCircle, XCircle, Search, X, Save, RefreshCw, Puzzle, Warehouse,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = { starter: 'bg-gray-100 text-gray-600', pro: 'bg-blue-100 text-blue-700', enterprise: 'bg-purple-100 text-purple-700' };

const ALL_MODULES = [
  { key: 'performance',     label: 'Customer Performance Analytics',  desc: 'Top-10 charts and revenue analytics' },
  { key: 'mt_tracking',     label: 'MT Cylinder Tracking',            desc: 'Empty cylinder tracking per vendor' },
  { key: 'attendance',      label: 'Attendance & HR',                 desc: 'Daily check-ins and monthly summaries' },
  { key: 'daily_closing',   label: 'Daily Closing Reports',           desc: 'End-of-day financial summary' },
  { key: 'stock_movement',  label: 'Stock Movement Reports',          desc: 'In/out movement history' },
  { key: 'downloads',       label: 'Downloads & Export',              desc: 'CSV export for all reports' },
  { key: 'masters',         label: 'Masters Configuration',           desc: 'Brands, employees, cylinder sizes' },
  { key: 'calendar',        label: 'Calendar & Schedule',             desc: 'Team schedule and sales calendar' },
  { key: 'sale_calculator', label: 'Sale Calculator',                 desc: 'Quick sale amount estimator' },
];

function StatCard({ label, value, sub, color = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="text-[11px] font-bold tracking-widest text-gray-400 uppercase mb-2">{label}</div>
      <div className={`font-black text-3xl ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function UserRow({ user, onDelete, onToggle }) {
  const roleColors = { admin:'bg-black text-white', manager:'bg-blue-100 text-blue-700', stockist:'bg-green-100 text-green-700', driver:'bg-orange-100 text-orange-700' };
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="text-[13px] font-medium text-gray-900">{user.name}</div>
        <div className="text-xs text-gray-400">{user.email}</div>
      </td>
      <td className="px-4 py-3">
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded capitalize ${roleColors[user.role]||'bg-gray-100 text-gray-600'}`}>{user.role}</span>
      </td>
      <td className="px-4 py-3 text-[13px] text-gray-500">{user.godown || '—'}</td>
      <td className="px-4 py-3">
        <button onClick={() => onToggle(user)} className={`text-[11px] font-bold px-2 py-0.5 rounded ${user.is_active ? 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700' : 'bg-red-100 text-red-700 hover:bg-green-100 hover:text-green-700'} transition-colors`}>
          {user.is_active ? 'Active' : 'Inactive'}
        </button>
      </td>
      <td className="px-4 py-3">
        <button onClick={() => onDelete(user.id)} className="text-gray-300 hover:text-red-500 transition-colors">
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

export default function SuperAdminDashboard() {
  const { saAuth, saLogout, saAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [expandedId, setExpandedId]       = useState(null);
  const [expandedTab, setExpandedTab]     = useState('users'); // 'users' | 'modules'
  const [companyUsers, setCompanyUsers]   = useState({});
  const [companyModules, setCompanyModules] = useState({}); // { [cid]: { key: bool } }
  const [modulesSaving, setModulesSaving] = useState(null);
  const [godownInputs, setGodownInputs]   = useState({}); // { [cid]: newGodownName }
  const [search, setSearch]               = useState('');
  const [showAdd, setShowAdd]             = useState(false);
  const [showAddUser, setShowAddUser]     = useState(null);
  const [saving, setSaving]               = useState(false);
  const [successMsg, setSuccessMsg]       = useState('');

  const [newCo, setNewCo]   = useState({ name:'', code:'', plan:'starter', contact_phone:'', godowns:'' });
  const [newUser, setNewUser] = useState({ name:'', email:'', password:'', role:'admin', godown:'' });

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/companies`, { headers: { Authorization: `Bearer ${saAuth?.token}` } });
      setCompanies(res.data);
    } finally { setLoading(false); }
  }, [saAuth?.token]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const fetchUsers = async (cid) => {
    if (companyUsers[cid]) return;
    const res = await axios.get(`${API}/superadmin/companies/${cid}/users`, { headers: saAuthHeader() });
    setCompanyUsers(u => ({ ...u, [cid]: res.data }));
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    setExpandedTab('users');
    await fetchUsers(id);
    // Init module state from company object
    const co = companies.find(c => c.id === id);
    if (co && !companyModules[id]) {
      const mods = {};
      ALL_MODULES.forEach(m => { mods[m.key] = co.enabled_modules ? co.enabled_modules[m.key] !== false : true; });
      setCompanyModules(prev => ({ ...prev, [id]: mods }));
    }
  };

  const toggleModule = (cid, key) => {
    setCompanyModules(prev => ({ ...prev, [cid]: { ...prev[cid], [key]: !prev[cid][key] } }));
  };

  const saveModules = async (cid) => {
    setModulesSaving(cid);
    try {
      await axios.patch(`${API}/superadmin/companies/${cid}/modules`, companyModules[cid], { headers: saAuthHeader() });
      setCompanies(cs => cs.map(c => c.id === cid ? { ...c, enabled_modules: companyModules[cid] } : c));
      flash('Modules saved!');
    } catch { alert('Failed to save modules'); }
    finally { setModulesSaving(null); }
  };

  const addGodown = async (cid) => {
    const name = (godownInputs[cid] || '').trim();
    if (!name) return;
    const company = companies.find(c => c.id === cid);
    const updated = [...(company?.godowns || []), name];
    try {
      await axios.patch(`${API}/superadmin/companies/${cid}`, { godowns: updated }, { headers: saAuthHeader() });
      setCompanies(cs => cs.map(c => c.id === cid ? { ...c, godowns: updated } : c));
      setGodownInputs(gi => ({ ...gi, [cid]: '' }));
      flash(`Godown "${name}" added!`);
    } catch { alert('Failed to add godown'); }
  };

  const removeGodown = async (cid, godown) => {
    if (!window.confirm(`Remove godown "${godown}"? This won't delete existing stock data.`)) return;
    const company = companies.find(c => c.id === cid);
    const updated = (company?.godowns || []).filter(g => g !== godown);
    try {
      await axios.patch(`${API}/superadmin/companies/${cid}`, { godowns: updated }, { headers: saAuthHeader() });
      setCompanies(cs => cs.map(c => c.id === cid ? { ...c, godowns: updated } : c));
      flash(`Godown removed.`);
    } catch { alert('Failed to remove godown'); }
  };

  const toggleActive = async (company) => {
    const newState = !company.is_active;
    await axios.patch(`${API}/superadmin/companies/${company.id}`,
      { is_active: newState, disabled_reason: newState ? '' : 'Disabled by admin' },
      { headers: saAuthHeader() }
    );
    setCompanies(cs => cs.map(c => c.id === company.id ? { ...c, is_active: newState } : c));
    flash(`${company.name} ${newState ? 'enabled' : 'paused'}`);
  };

  const deleteCompany = async (id) => {
    if (!window.confirm('Delete this company and all its users?')) return;
    await axios.delete(`${API}/superadmin/companies/${id}`, { headers: saAuthHeader() });
    setCompanies(cs => cs.filter(c => c.id !== id));
    if (expandedId === id) setExpandedId(null);
    flash('Company deleted');
  };

  const addCompany = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...newCo, code: newCo.code.toUpperCase(),
        godowns: newCo.godowns.split(',').map(g => g.trim()).filter(Boolean),
      };
      const res = await axios.post(`${API}/superadmin/companies`, payload, { headers: saAuthHeader() });
      setCompanies(cs => [...cs, { ...res.data, user_count: 0 }]);
      setNewCo({ name:'', code:'', plan:'starter', contact_phone:'', godowns:'' });
      setShowAdd(false);
      flash('Company created!');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error creating company');
    } finally { setSaving(false); }
  };

  const addUser = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const res = await axios.post(`${API}/superadmin/companies/${showAddUser}/users`, newUser, { headers: saAuthHeader() });
      setCompanyUsers(u => ({ ...u, [showAddUser]: [...(u[showAddUser] || []), res.data] }));
      setCompanies(cs => cs.map(c => c.id === showAddUser ? { ...c, user_count: (c.user_count||0)+1 } : c));
      setNewUser({ name:'', email:'', password:'', role:'admin', godown:'' });
      setShowAddUser(null);
      flash('User added!');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error adding user');
    } finally { setSaving(false); }
  };

  const deleteUser = async (cid, uid) => {
    await axios.delete(`${API}/superadmin/companies/${cid}/users/${uid}`, { headers: saAuthHeader() });
    setCompanyUsers(u => ({ ...u, [cid]: (u[cid]||[]).filter(x => x.id !== uid) }));
    setCompanies(cs => cs.map(c => c.id === cid ? { ...c, user_count: Math.max(0,(c.user_count||1)-1) } : c));
    flash('User removed');
  };

  const toggleUser = async (cid, user) => {
    await axios.patch(`${API}/superadmin/companies/${cid}/users/${user.id}`,
      { is_active: !user.is_active }, { headers: saAuthHeader() }
    );
    setCompanyUsers(u => ({
      ...u, [cid]: (u[cid]||[]).map(x => x.id === user.id ? { ...x, is_active: !x.is_active } : x)
    }));
  };

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search.toUpperCase())
  );

  const stats = {
    total:    companies.length,
    active:   companies.filter(c => c.is_active).length,
    inactive: companies.filter(c => !c.is_active).length,
    users:    companies.reduce((s, c) => s + (c.user_count||0), 0),
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white';
  const sel = inp;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Nav */}
      <header className="bg-white border-b border-gray-200 px-6 py-3.5 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
            <Flame size={16} className="text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-base tracking-tight">VAYUSARTHI</span>
          <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-full px-2.5 py-0.5">
            <Shield size={11} className="text-orange-500" />
            <span className="text-[11px] font-bold text-orange-600 uppercase tracking-wider">Super Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {successMsg && (
            <span className="flex items-center gap-1.5 text-green-700 text-[13px] font-medium bg-green-50 px-3 py-1 rounded-full">
              <CheckCircle size={13} /> {successMsg}
            </span>
          )}
          <span className="text-sm text-gray-500">{saAuth?.admin?.name}</span>
          <button onClick={() => { saLogout(); navigate('/superadmin/login'); }}
            className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-900 transition-colors">
            <LogOut size={14} /> Log out
          </button>
        </div>
      </header>

      <div className="px-6 py-6 max-w-screen-xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-7">
          <StatCard label="Total Companies" value={stats.total} />
          <StatCard label="Active" value={stats.active} color="text-green-600" />
          <StatCard label="Paused" value={stats.inactive} color="text-red-600" />
          <StatCard label="Total Users" value={stats.users} />
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <h2 className="font-black text-xl tracking-tight whitespace-nowrap">Companies</h2>
            <div className="relative flex-1 max-w-xs">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input className="pl-8 pr-3 py-2 border border-gray-200 rounded-md text-[13px] w-full focus:outline-none focus:border-gray-400 bg-white"
                placeholder="Search by name or code…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchCompanies} className="btn-secondary flex items-center gap-1.5 text-sm">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={() => setShowAdd(!showAdd)} className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus size={14} /> Add company
            </button>
          </div>
        </div>

        {/* Add company panel */}
        {showAdd && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-base tracking-tight">New Company</h3>
              <button onClick={() => setShowAdd(false)}><X size={16} className="text-gray-400 hover:text-gray-700" /></button>
            </div>
            <form onSubmit={addCompany}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                <div><label className="small-label block mb-1">Company Name</label><input className={inp} value={newCo.name} onChange={e=>setNewCo(c=>({...c,name:e.target.value}))} placeholder="e.g. Mumbai LPG Co." required /></div>
                <div>
                  <label className="small-label block mb-1">Company Code <span className="text-gray-400 normal-case font-normal">(login code)</span></label>
                  <input className={inp + ' uppercase font-semibold tracking-widest'} value={newCo.code} onChange={e=>setNewCo(c=>({...c,code:e.target.value.toUpperCase()}))} placeholder="e.g. MUM" maxLength={8} required />
                </div>
                <div><label className="small-label block mb-1">Plan</label>
                  <select className={sel} value={newCo.plan} onChange={e=>setNewCo(c=>({...c,plan:e.target.value}))}>
                    <option value="starter">Starter</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div><label className="small-label block mb-1">Phone</label><input className={inp} value={newCo.contact_phone} onChange={e=>setNewCo(c=>({...c,contact_phone:e.target.value}))} placeholder="Contact number" /></div>
                <div className="sm:col-span-2"><label className="small-label block mb-1">Godowns <span className="normal-case font-normal text-gray-400">(comma-separated)</span></label><input className={inp} value={newCo.godowns} onChange={e=>setNewCo(c=>({...c,godowns:e.target.value}))} placeholder="e.g. Godown A, Godown B" /></div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={()=>setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary flex items-center gap-1.5 text-sm" disabled={saving}>
                  <Save size={13} />{saving ? 'Creating…' : 'Create company'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Add user modal */}
        {showAddUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-black text-lg tracking-tight">Add User</h3>
                <button onClick={() => setShowAddUser(null)}><X size={16} className="text-gray-400 hover:text-gray-700" /></button>
              </div>
              <form onSubmit={addUser} className="space-y-3">
                <div><label className="small-label block mb-1">Full Name</label><input className={inp} value={newUser.name} onChange={e=>setNewUser(u=>({...u,name:e.target.value}))} required /></div>
                <div><label className="small-label block mb-1">Email</label><input className={inp} type="email" value={newUser.email} onChange={e=>setNewUser(u=>({...u,email:e.target.value}))} required /></div>
                <div><label className="small-label block mb-1">Password</label><input className={inp} type="password" value={newUser.password} onChange={e=>setNewUser(u=>({...u,password:e.target.value}))} required /></div>
                <div><label className="small-label block mb-1">Role</label>
                  <select className={sel} value={newUser.role} onChange={e=>setNewUser(u=>({...u,role:e.target.value}))}>
                    <option value="admin">Admin</option><option value="manager">Manager</option>
                    <option value="stockist">Stockist</option><option value="driver">Driver</option>
                  </select>
                </div>
                {newUser.role === 'stockist' && (
                  <div><label className="small-label block mb-1">Assigned Godown</label><input className={inp} value={newUser.godown} onChange={e=>setNewUser(u=>({...u,godown:e.target.value}))} placeholder="e.g. Ambhai" required /></div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowAddUser(null)} className="btn-secondary text-sm">Cancel</button>
                  <button type="submit" className="btn-primary flex items-center gap-1.5 text-sm" disabled={saving}>
                    <Save size={13} />{saving ? 'Adding…' : 'Add user'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Company table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-400 text-sm">Loading companies…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">No companies found</div>
          ) : (
            filtered.map((c) => (
              <div key={c.id} className="border-b border-gray-100 last:border-0">
                {/* Company row */}
                <div className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                  <button onClick={() => toggleExpand(c.id)} className="text-gray-400 hover:text-gray-700">
                    {expandedId === c.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-[14px]">{c.name}</span>
                      <span className="text-[11px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono tracking-widest">{c.code}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded capitalize ${PLAN_COLORS[c.plan]||'bg-gray-100 text-gray-600'}`}>{c.plan}</span>
                    </div>
                    {c.contact_phone && <div className="text-xs text-gray-400 mt-0.5">{c.contact_phone}</div>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Users size={12} />
                    <span>{c.user_count || 0}</span>
                  </div>
                  <div className="text-xs text-gray-400">{c.created_at?.split('T')[0]}</div>
                  {/* Toggle */}
                  <button onClick={() => toggleActive(c)} title={c.is_active ? 'Pause company' : 'Enable company'}
                    className={`flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${c.is_active ? 'text-green-600 hover:text-red-600' : 'text-red-600 hover:text-green-600'}`}>
                    {c.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    {c.is_active ? 'Active' : 'Paused'}
                  </button>
                  <button onClick={() => deleteCompany(c.id)} className="text-gray-300 hover:text-red-500 transition-colors ml-1">
                    <Trash2 size={15} />
                  </button>
                </div>

                {/* Expanded panel: Users + Modules tabs */}
                {expandedId === c.id && (
                  <div className="border-t border-gray-100 bg-gray-50 px-8 py-4">
                    {/* Tab switcher */}
                    <div className="flex gap-1 mb-4 bg-white border border-gray-200 rounded-lg p-1 w-fit">
                      <button onClick={() => setExpandedTab('users')}
                        className={`px-4 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5 transition-all ${expandedTab==='users'?'bg-gray-900 text-white':'text-gray-500 hover:text-gray-700'}`}>
                        <Users size={12} /> Users
                      </button>
                      <button onClick={() => setExpandedTab('modules')}
                        className={`px-4 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5 transition-all ${expandedTab==='modules'?'bg-gray-900 text-white':'text-gray-500 hover:text-gray-700'}`}>
                        <Puzzle size={12} /> Modules
                      </button>
                      <button onClick={() => setExpandedTab('godowns')}
                        className={`px-4 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5 transition-all ${expandedTab==='godowns'?'bg-gray-900 text-white':'text-gray-500 hover:text-gray-700'}`}>
                        <Warehouse size={12} /> Godowns
                      </button>
                    </div>

                    {/* USERS TAB */}
                    {expandedTab === 'users' && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <div className="small-label text-gray-500">Users in {c.name}</div>
                          <button onClick={() => { setShowAddUser(c.id); setNewUser({ name:'',email:'',password:'',role:'admin',godown:'' }); }}
                            className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
                            <Plus size={12} /> Add user
                          </button>
                        </div>
                        {!companyUsers[c.id] ? (
                          <div className="text-xs text-gray-400 py-2">Loading…</div>
                        ) : companyUsers[c.id].length === 0 ? (
                          <div className="text-xs text-gray-400 py-2">No users yet — add one above</div>
                        ) : (
                          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead><tr className="border-b border-gray-100 bg-gray-50">
                                {['Name / Email','Role','Godown','Status',''].map(h=>(
                                  <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400">{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {companyUsers[c.id].map(u => (
                                  <UserRow key={u.id} user={u}
                                    onDelete={(uid) => deleteUser(c.id, uid)}
                                    onToggle={(user) => toggleUser(c.id, user)}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}

                    {/* MODULES TAB */}
                    {expandedTab === 'modules' && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <div className="small-label text-gray-500">App Modules for {c.name}</div>
                          <button onClick={() => saveModules(c.id)} disabled={modulesSaving === c.id}
                            className="btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5">
                            {modulesSaving === c.id ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
                            {modulesSaving === c.id ? 'Saving…' : 'Save Changes'}
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {ALL_MODULES.map(mod => {
                            const enabled = companyModules[c.id]?.[mod.key] !== false;
                            return (
                              <div key={mod.key} data-testid={`module-toggle-${mod.key}`}
                                onClick={() => toggleModule(c.id, mod.key)}
                                className={`flex items-start gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all select-none ${enabled ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-gray-50 border-gray-200 opacity-60 hover:opacity-80'}`}>
                                <div className={`mt-0.5 flex-shrink-0 w-8 h-4 rounded-full transition-all relative ${enabled ? 'bg-black' : 'bg-gray-300'}`}>
                                  <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${enabled ? 'left-4' : 'left-0.5'}`} />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-[13px] font-semibold text-gray-800 leading-tight">{mod.label}</div>
                                  <div className="text-[11px] text-gray-400 mt-0.5">{mod.desc}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    {/* GODOWNS TAB */}
                    {expandedTab === 'godowns' && (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <div className="small-label text-gray-500">Godowns for {c.name}</div>
                          <span className="text-xs text-gray-400">{(c.godowns || []).length} godown{(c.godowns || []).length !== 1 ? 's' : ''}</span>
                        </div>

                        {/* Add godown form */}
                        <div className="flex items-center gap-2 mb-4">
                          <input
                            data-testid="add-godown-input"
                            value={godownInputs[c.id] || ''}
                            onChange={e => setGodownInputs(gi => ({ ...gi, [c.id]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && addGodown(c.id)}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white"
                            placeholder="Godown name e.g. Thane, Pune West…"
                          />
                          <button
                            data-testid="add-godown-btn"
                            onClick={() => addGodown(c.id)}
                            disabled={!(godownInputs[c.id] || '').trim()}
                            className="btn-primary flex items-center gap-1.5 text-sm px-4 py-2 disabled:opacity-40"
                          >
                            <Plus size={13} /> Add Godown
                          </button>
                        </div>

                        {/* Godown list */}
                        {(c.godowns || []).length === 0 ? (
                          <div className="text-xs text-gray-400 py-3 text-center bg-white border border-gray-200 rounded-lg">
                            No godowns yet — add one above.
                          </div>
                        ) : (
                          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            {(c.godowns || []).map((g, gi) => (
                              <div key={gi} data-testid={`godown-row-${g}`}
                                className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                                <div className="flex items-center gap-2.5">
                                  <Warehouse size={14} className="text-gray-300 flex-shrink-0" />
                                  <span className="text-[13px] font-medium text-gray-800">{g}</span>
                                </div>
                                <button onClick={() => removeGodown(c.id, g)}
                                  className="text-gray-300 hover:text-red-500 transition-colors p-1 rounded"
                                  title="Remove godown">
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
